import { ImportStatusEnum, ImportWithAirport, ImportWithAirportStatusEnum, ImportWithAirportTypeEnum } from '@/models/gen/graphql';

import { DATE_FE_FORMAT } from '@/constants';
import { Dayts } from '@/utils';
import { searchManifestTableWithMetadata } from '@/features/Manifests/api/services/searchManifestTable';

export namespace ManifestsTypes {
  export type Filters = {
    from: string;
    to: string;
    createdBy: string;
    airportCode: string[];
    providerId: string;
    type?: ImportWithAirportTypeEnum;
    status?: ImportWithAirportStatusEnum[];
    archived?: boolean;
  };
  export const initFilters: Filters = {
    from: new Dayts().startOf('day').format(DATE_FE_FORMAT),
    to: new Dayts().endOf('day').format(DATE_FE_FORMAT),
    createdBy: '',
    airportCode: [],
    providerId: '',
    type: ImportWithAirportTypeEnum.Manifest,
    status: [
      ImportWithAirportStatusEnum.Ingesting,
      ImportWithAirportStatusEnum.NotParsed,
      ImportWithAirportStatusEnum.Parsed,
      ImportWithAirportStatusEnum.Potential,
      ImportWithAirportStatusEnum.Staged,
      ImportWithAirportStatusEnum.Staging,
      ImportWithAirportStatusEnum.Uploaded,
    ],
    archived: false,
  };
  export type Modals = {
    manifestUpload: {
      show: boolean;
    } | null;
    manifestOverrides: {
      show: boolean;
      manifestId?: string;
      name?: string;
      status?: string;
    } | null;
    manifestSettings: {
      show: boolean;
    } | null;
  };
  export const initModals: Modals = {
    manifestUpload: null,
    manifestOverrides: null,
    manifestSettings: null,
  };
  export type Settings = {
    rememberFilters: boolean;
    columns: {
      SELECT: boolean;
      DATE: boolean;
      STATUS: boolean;
      PROVIDER: boolean;
      IMPORTED_BY: boolean;
      FROM: boolean;
      SUBJECT: boolean;
      TYPE: boolean;
      DOWNLOAD: boolean;
    };
  };

  export type State = {
    imports: ImportWithAirport[];
    total: number;
    selected: string[];
    loading: boolean;
    filters: Filters;
    table: Table;
    modals: Modals;
    settings: Settings;
    fetch: () => Promise<void>;
    onSelect: (id: string) => void;
    onReset: () => void;
  };
  export const initSettings: Settings = {
    rememberFilters: true,
    columns: {
      SELECT: true,
      DATE: true,
      STATUS: true,
      PROVIDER: true,
      IMPORTED_BY: true,
      FROM: true,
      SUBJECT: true,
      TYPE: true,
      DOWNLOAD: true,
    },
  };

  export const initState = (setState, getState) => {
    const fetch = async (): Promise<void> => {
      try {
        setState((current) => ({ ...current, loading: true }));
        const { state } = getState();
        const filters = { ...state.filters };
        filters.from = new Dayts(filters.from).startOf('day').toISOString();
        filters.to = new Dayts(filters.to).endOf('day').add(1, 'day').toISOString();
        const res = await searchManifestTableWithMetadata(filters);
        const { imports, totalCount } = res;
        setState({ ...state, imports, total: totalCount, selected: [] });
      } catch (err) {
        console.error('Error fetching imports:', err);
      } finally {
        setState((current) => ({ ...current, loading: false }));
      }
    };
    return {
      imports: [],
      total: 0,
      selected: [],
      loading: false,
      filters: initFilters,
      table: initTable,
      modals: initModals,
      settings: initSettings,
      fetch,
      onSelect: (id: string): void => {
        setState((current) => ({
          ...current,
          selected: current.selected.includes(id) ? current.selected.filter((val) => val !== id) : [...current.selected, id],
        }));
      },
      onReset: (): void => {
        setState((current) => ({ ...current, filters: initFilters }));
        fetch();
      },
    };
  };
  export type Query = {
    from: string;
    to: string;
    createdBy: string;
    airportCode: string[];
    providerId: string;
    type?: ImportWithAirportTypeEnum;
    status?: ImportWithAirportStatusEnum[];
    archived?: boolean;
  };
  export type Table = {
    sorting: {
      column: string | undefined;
      direction: 'asc' | 'desc' | undefined;
    };
  };
  export const initTable: Table = {
    sorting: {
      column: 'createdAt',
      direction: 'desc',
    },
  };
  export const ReadyToImportStatuses = [ImportStatusEnum.Potential, ImportStatusEnum.Staged, ImportStatusEnum.NotParsed];
}
